export const WHATSAPP_TEMPLATE_PARAMETER_FORMAT = {
  NAMED: "named",
  POSITIONAL: "positional",
} as const;

export type WhatsappTemplateParameterFormat =
  (typeof WHATSAPP_TEMPLATE_PARAMETER_FORMAT)[keyof typeof WHATSAPP_TEMPLATE_PARAMETER_FORMAT];

export const WHATSAPP_TEMPLATE_CATEGORY = {
  MARKETING: "marketing",
  UTILITY: "utility",
  AUTHENTICATION: "authentication",
} as const;

export type WhatsappTemplateCategory =
  (typeof WHATSAPP_TEMPLATE_CATEGORY)[keyof typeof WHATSAPP_TEMPLATE_CATEGORY];

export const WHATSAPP_TEMPLATE_LANGUAGE = {
  ENGLISH: "en",
  ENGLISH_US: "en_US",
  ARABIC: "ar",
} as const;

export type WhatsappTemplateLanguage =
  (typeof WHATSAPP_TEMPLATE_LANGUAGE)[keyof typeof WHATSAPP_TEMPLATE_LANGUAGE];

export const WHATSAPP_TEMPLATE_HEADER_FORMAT = {
  TEXT: "text",
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
  LOCATION: "location",
} as const;

export type WhatsappTemplateHeaderFormat =
  (typeof WHATSAPP_TEMPLATE_HEADER_FORMAT)[keyof typeof WHATSAPP_TEMPLATE_HEADER_FORMAT];

export const WHATSAPP_TEMPLATE_COMPONENT_TYPE = {
  HEADER: "header",
  BODY: "body",
  FOOTER: "footer",
  BUTTONS: "buttons",
  LIMITED_TIME_OFFER: "limited_time_offer",
  CAROUSEL: "carousel",
} as const;

export type WhatsappTemplateComponentType =
  (typeof WHATSAPP_TEMPLATE_COMPONENT_TYPE)[keyof typeof WHATSAPP_TEMPLATE_COMPONENT_TYPE];

export const WHATSAPP_TEMPLATE_BUTTON_TYPE = {
  QUICK_REPLY: "quick_reply",
  URL: "url",
  PHONE_NUMBER: "phone_number",
  COPY_CODE: "copy_code",
  OTP: "otp",
} as const;

export type WhatsappTemplateButtonType =
  (typeof WHATSAPP_TEMPLATE_BUTTON_TYPE)[keyof typeof WHATSAPP_TEMPLATE_BUTTON_TYPE];

export const WHATSAPP_TEMPLATE_STATUS = {
  ARCHIVED: "archived",
  APPROVED: "approved",
  DELETED: "deleted",
  DISABLED: "disabled",
  FLAGGED: "flagged",
  IN_APPEAL: "in_appeal",
  LIMIT_EXCEEDED: "limit_exceeded",
  LOCKED: "locked",
  PAUSED: "paused",
  PENDING: "pending",
  REINSTATED: "reinstated",
  PENDING_DELETION: "pending_deletion",
  REJECTED: "rejected",
} as const;

export type WhatsappTemplateStatus =
  (typeof WHATSAPP_TEMPLATE_STATUS)[keyof typeof WHATSAPP_TEMPLATE_STATUS];

export const WHATSAPP_TEMPLATE_QUALITY_SCORE = {
  UNKNOWN: "unknown",
  GREEN: "green",
  YELLOW: "yellow",
  RED: "red",
} as const;

export type WhatsappTemplateQualityScore =
  (typeof WHATSAPP_TEMPLATE_QUALITY_SCORE)[keyof typeof WHATSAPP_TEMPLATE_QUALITY_SCORE];

// Types
export type WhatsappTemplateQuickReplyButton = {
  type: "quick_reply";
  text: string;
};

export type WhatsappTemplateUrlButton = {
  type: "url";
  text: string;
  url: string;
  example?: string[];
};

export type WhatsappTemplatePhoneNumberButton = {
  type: "phone_number";
  text: string;
  phoneNumber: string;
};

export type WhatsappTemplateCopyCodeButton = {
  type: "copy_code";
  text: string;
  couponCode?: string;
  example?: string[];
};

export type WhatsappTemplateButton =
  | WhatsappTemplateQuickReplyButton
  | WhatsappTemplateUrlButton
  | WhatsappTemplatePhoneNumberButton
  | WhatsappTemplateCopyCodeButton;

export type LocationPayload = {
  name: string;
  address: string;
  latitude: string;
  longitude: string;
};

export type WhatsappTemplateHeaderComponent = {
  type: "header";
  format: WhatsappTemplateHeaderFormat;
  text?: string;
  location?: LocationPayload;
  link?: string;
  example?: {
    headerHandle?: Array<string>;
    headerText?: Array<string>;
    headerTextNamedParams?: Array<{
      paramName: string;
      example: string;
    }>;
  };
};

export type WhatsappTemplateBodyComponent = {
  type: "body";
  text: string;
  example?: {
    bodyText?: Array<string>;
    bodyTextNamedParams?: Array<{
      paramName: string;
      example: string;
    }>;
  };
};

export type WhatsappTemplateFooterComponent = {
  type: "footer";
  text: string;
};

export type WhatsappTemplateButtonsComponent = {
  type: "buttons";
  buttons: WhatsappTemplateButton[];
};

export type WhatsappTemplateCarouselComponent = {
  type: "carousel";
};

export type WhatsappTemplateLimitedTimeOfferComponent = {
  type: "limited_time_offer";
};

export type WhatsappTemplateComponent =
  | WhatsappTemplateHeaderComponent
  | WhatsappTemplateBodyComponent
  | WhatsappTemplateFooterComponent
  | WhatsappTemplateButtonsComponent
  | WhatsappTemplateCarouselComponent
  | WhatsappTemplateLimitedTimeOfferComponent;

export type WhatsappTemplate = {
  id: string;
  name: string;
  externalId: string;
  channelId: string;
  organizationId: string;
  workspaceId: string;
  parameterFormat: WhatsappTemplateParameterFormat;
  language: string;
  status: WhatsappTemplateStatus;
  qualityScore: WhatsappTemplateQualityScore;
  qualityScoreDate: string | null;
  components: WhatsappTemplateComponent[];
  createdAt: string;
  updatedAt: string;
  lastEditedAt: string | null;
  createdBy: string | null;
  lastEditedBy: string | null;
  deletedAt: string | null;
  category: WhatsappTemplateCategory;
  lastUsedAt: string | null;
  /** Meta's reason code when it rejects the template, like `PROMOTIONAL`. */
  rejectedReason?: string | null;
  /** Meta's own words on a rejection, when it gives them. */
  rejectionInfo?: { reason: string; recommendation: string } | null;
};

// Creation types
export type CreateWhatsappTemplatePayload = {
  id?: string;
  name: string;
  parameterFormat: WhatsappTemplateParameterFormat;
  language: string;
  category: WhatsappTemplateCategory;
  components: WhatsappTemplateComponentCreate[];
};

export type WhatsappTemplateComponentCreate =
  | WhatsappTemplateHeaderComponentCreate
  | WhatsappTemplateBodyComponentCreate
  | WhatsappTemplateFooterComponentCreate
  | WhatsappTemplateButtonsComponentCreate
  | WhatsappTemplateCarouselComponent
  | WhatsappTemplateLimitedTimeOfferComponent;

export type WhatsappTemplateHeaderComponentCreate = {
  type: "header";
  format: WhatsappTemplateHeaderFormat;
  text?: string;
  example?: {
    headerTextNamedParams?: Array<{
      paramName: string;
      example: string;
    }>;
    headerText?: Array<string>;
    headerHandle?: Array<string>;
  };
};

export type WhatsappTemplateBodyComponentCreate = {
  type: "body";
  text?: string;
  example?: {
    bodyTextNamedParams?: Array<{
      paramName: string;
      example: string;
    }>;
    bodyText?: Array<string>;
  };
  addSecurityRecommendation?: boolean;
};

export type WhatsappTemplateButtonsComponentCreate = {
  type: "buttons";
  buttons: Array<WhatsappTemplateButtonComponentCreate>;
};

export type WhatsappTemplateButtonComponentCreate = {
  type: WhatsappTemplateButtonType;
  example?: string | Array<string>;
  text?: string;
  url?: string;
  phoneNumber?: string;
  otpType?: "copy_code";
};

export type WhatsappTemplateFooterComponentCreate = {
  type: "footer";
  text?: string;
  codeExpirationMinutes?: number;
};

export type ResolvedWhatsappTemplatePayload = {
  templatePayload: WhatsappTemplateComponent[];
  deliveryPayload: any[];
};

export type UploadMetaAssetResponse = {
  data: {
    h: string;
  };
  message: string;
};
